import { Controller } from 'stimulus'

export default class extends Controller {
  declare urlValue: string
  declare resultsTarget: HTMLElement
  declare loadingTarget: HTMLElement
  declare nameInputTarget: HTMLInputElement
  declare bundeslandSelectTarget: HTMLSelectElement
  declare sectorSelectTarget: HTMLSelectElement
  declare organisationTypeSelectTarget: HTMLSelectElement
  declare sortActivityTarget: HTMLButtonElement
  declare sortNameTarget: HTMLButtonElement
  declare entryListTarget: HTMLElement
  declare showMoreContainerTarget: HTMLElement
  declare hasShowMoreContainerTarget: boolean
  declare showMoreBtnTarget: HTMLButtonElement
  declare hasShowMoreBtnTarget: boolean
  declare countTarget: HTMLElement
  declare searchBtnTarget: HTMLButtonElement
  declare searchBtnIconTarget: HTMLElement
  declare searchBtnSpinnerTarget: HTMLElement

  static values = { url: String }
  static targets = [
    'results', 'loading', 'nameInput', 'bundeslandSelect',
    'sectorSelect', 'organisationTypeSelect',
    'sortActivity', 'sortName', 'entryList', 'showMoreContainer',
    'showMoreBtn', 'count', 'searchBtn', 'searchBtnIcon', 'searchBtnSpinner'
  ]

  private debounceTimer: ReturnType<typeof setTimeout> | null = null
  private currentSort = ''
  private cache = new Map<string, { html: string; count: number; remaining: number }>()

  connect() {
    const params = new URLSearchParams(window.location.search)
    this.currentSort = params.get('sort') || ''
  }

  debouncedSearch() {
    if (this.debounceTimer) clearTimeout(this.debounceTimer)
    this.debounceTimer = setTimeout(() => this.search(), 300)
  }

  submitOnEnter(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault()
      if (this.debounceTimer) clearTimeout(this.debounceTimer)
      this.search()
    }
  }

  search() {
    const qs = this.buildQueryString()
    qs.set('offset', '0')

    const vanityUrl = this.vanityUrlFor(qs)
    if (vanityUrl) {
      window.Turbo?.visit(vanityUrl)
      return
    }

    const cacheKey = qs.toString()
    const cached = this.cache.get(cacheKey)
    if (cached) {
      this.resultsTarget.innerHTML = cached.html
      this.updateUrl(qs)
      return
    }

    this.setLoading(true)
    this.resultsTarget.innerHTML = ''

    const url = `${this.urlValue}?${qs.toString()}&format=json`
    fetch(url)
      .then(res => res.json())
      .then((data: { html: string; count: number; remaining: number }) => {
        this.cache.set(cacheKey, data)
        this.setLoading(false)
        this.resultsTarget.innerHTML = data.html
      })
      .catch(() => {
        this.setLoading(false)
      })

    this.updateUrl(qs)
  }

  setSort(event: Event) {
    const target = event.currentTarget as HTMLElement
    this.currentSort = target.dataset.sortValue || ''

    this.sortActivityTarget.className = this.currentSort !== 'name' ? 'btn btn-primary' : 'btn btn-outline-primary'
    this.sortNameTarget.className = this.currentSort === 'name' ? 'btn btn-primary' : 'btn btn-outline-primary'

    this.search()
  }

  reset() {
    this.nameInputTarget.value = ''
    this.bundeslandSelectTarget.value = ''
    this.sectorSelectTarget.value = ''
    this.organisationTypeSelectTarget.value = ''
    this.currentSort = ''

    this.sortActivityTarget.className = 'btn btn-primary'
    this.sortNameTarget.className = 'btn btn-outline-primary'

    this.search()
  }

  showMore(event: Event) {
    const btn = event.currentTarget as HTMLButtonElement
    const offset = parseInt(btn.dataset.offset || '20', 10)
    const qs = this.buildQueryString()
    qs.set('offset', String(offset))

    btn.disabled = true
    btn.textContent = '...'

    const url = `${this.urlValue}?${qs.toString()}&format=json`
    fetch(url)
      .then(res => res.json())
      .then((data: { html: string; count: number; remaining: number }) => {
        this.entryListTarget.insertAdjacentHTML('beforeend', data.html)

        if (data.remaining > 0) {
          const newOffset = offset + 20
          btn.dataset.offset = String(newOffset)
          btn.textContent = `${data.remaining} weitere anzeigen`
          btn.disabled = false
        } else if (this.hasShowMoreContainerTarget) {
          this.showMoreContainerTarget.remove()
        }
      })
  }

  private vanityUrlFor(qs: URLSearchParams): string | null {
    const bundesland = qs.get('bundesland')
    const name = qs.get('name')
    const sector = qs.get('sector')
    const orgType = qs.get('organisation_type')

    if (name || orgType) return null
    if (!bundesland) {
      if (window.location.pathname !== '/firmen') return '/firmen'
      return null
    }

    const blSlug = this.parameterize(bundesland)
    let vanity: string

    if (sector) {
      const sectorOption = this.sectorSelectTarget.selectedOptions[0]
      if (!sectorOption || !sectorOption.value) return null
      const sectorName = sectorOption.textContent?.replace(/\s*\(\d+\)$/, '').trim() || ''
      if (!sectorName) return null
      const sectorSlug = this.parameterize(sectorName)
      vanity = `/${sectorSlug}-firmen-in-${blSlug}`
    } else {
      vanity = `/firmen-in-${blSlug}`
    }

    if (window.location.pathname === vanity) return null
    return vanity
  }

  private parameterize(str: string): string {
    return str
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/[\s-]+/g, '-')
      .replace(/^-+|-+$/g, '')
  }

  private buildQueryString(): URLSearchParams {
    const qs = new URLSearchParams()
    const name = this.nameInputTarget.value
    if (name) qs.set('name', name)
    const bundesland = this.bundeslandSelectTarget.value
    if (bundesland) qs.set('bundesland', bundesland)
    const sector = this.sectorSelectTarget.value
    if (sector) qs.set('sector', sector)
    const organisationType = this.organisationTypeSelectTarget.value
    if (organisationType) qs.set('organisation_type', organisationType)
    if (this.currentSort) qs.set('sort', this.currentSort)
    return qs
  }

  private updateUrl(qs: URLSearchParams) {
    qs.delete('offset')
    const path = window.location.pathname
    if (path.match(/^\/firmen-in-/) || path.match(/-firmen-in-/)) {
      qs.delete('bundesland')
    }
    if (path.match(/-firmen-in-/)) {
      qs.delete('sector')
    }
    const qsStr = qs.toString()
    const url = qsStr ? `${path}?${qsStr}` : path
    window.history.replaceState(null, '', url)
  }

  private setLoading(loading: boolean) {
    if (loading) {
      this.loadingTarget.classList.remove('d-none')
      this.searchBtnTarget.disabled = true
      this.searchBtnIconTarget.classList.add('d-none')
      this.searchBtnSpinnerTarget.classList.remove('d-none')
    } else {
      this.loadingTarget.classList.add('d-none')
      this.searchBtnTarget.disabled = false
      this.searchBtnIconTarget.classList.remove('d-none')
      this.searchBtnSpinnerTarget.classList.add('d-none')
    }
  }

}
